import React from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import {
  Lock,
  VpnKey,
  WaterDrop,
  History as HistoryIcon,
  Leaderboard as LeaderboardIcon,
  MenuBook,
  Description,
  Security,
  RocketLaunch,
  ManageSearch,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useThemeMode } from '../hooks/useThemeMode';

const SIDEBAR_WIDTH = 240;

const navItems = [
  { label: 'Quick Privacy', icon: <RocketLaunch sx={{ fontSize: 20 }} />, path: '/easy' },
  { label: 'Lock Funds', icon: <Lock sx={{ fontSize: 20 }} />, path: '/deposit' },
  { label: 'My Keys', icon: <VpnKey sx={{ fontSize: 20 }} />, path: '/dashboard' },
  { label: 'Manual Withdraw', icon: <ManageSearch sx={{ fontSize: 20 }} />, path: '/withdraw-v2' },
  { label: 'Faucet', icon: <WaterDrop sx={{ fontSize: 20 }} />, path: '/faucet' },
  { label: 'History', icon: <HistoryIcon sx={{ fontSize: 20 }} />, path: '/history' },
  { label: 'Leaderboard', icon: <LeaderboardIcon sx={{ fontSize: 20 }} />, path: '/leaderboard' },
];

const resourceItems = [
  { label: 'Guide', icon: <MenuBook sx={{ fontSize: 20 }} />, path: '/guide' },
  { label: 'Docs', icon: <Description sx={{ fontSize: 20 }} />, path: '/docs' },
];

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark } = useThemeMode();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  if (!isDesktop) return null;

  const accent = '#22c55e';
  const sidebarBg = isDark ? '#0B0E14' : '#f8f9fa';
  const sidebarBorder = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)';
  const sidebarHover = isDark ? '#1D2026' : '#eef0f2';
  const sidebarActive = isDark ? '#1D2026' : '#e8f4f8';

  return (
    <Box
      sx={{
        width: SIDEBAR_WIDTH,
        flexShrink: 0,
        bgcolor: sidebarBg,
        borderRight: `1px solid ${sidebarBorder}`,
        display: 'flex',
        flexDirection: 'column',
        pt: 3,
        position: 'fixed',
        left: 0,
        top: 64,
        height: 'calc(100vh - 64px)',
        overflowY: 'auto',
        zIndex: 1000,
      }}
    >
      {/* Branding */}
      <Box sx={{ px: 2.5, mb: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{
          width: 32, height: 32,
          bgcolor: isDark ? '#1D2026' : '#e8f4f8',
          border: `1px solid ${sidebarBorder}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Security sx={{ fontSize: 16, color: accent }} />
        </Box>
        <Box>
          <Typography sx={{ color: accent, fontWeight: 900, fontSize: '0.8rem', letterSpacing: '0.15em', textTransform: 'uppercase', lineHeight: 1.2 }}>
            InterNull
          </Typography>
          <Typography sx={{ fontSize: '0.6rem', color: isDark ? '#525252' : '#a3a3a3', fontWeight: 500 }}>
            Sovereign Privacy
          </Typography>
        </Box>
      </Box>

      {/* Navigation */}
      <Box sx={{ flex: 1 }}>
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <Box
              key={item.path}
              onClick={() => navigate(item.path)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 2.5, py: 1.5,
                cursor: 'pointer',
                color: isActive ? accent : (isDark ? '#525252' : '#a3a3a3'),
                bgcolor: isActive ? sidebarActive : 'transparent',
                borderLeft: isActive ? `3px solid ${accent}` : '3px solid transparent',
                fontWeight: isActive ? 600 : 500,
                fontSize: '0.85rem',
                transition: 'all 0.15s',
                '&:hover': {
                  color: accent,
                  bgcolor: sidebarHover,
                },
              }}
            >
              {item.icon}
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 'inherit', letterSpacing: '0.03em' }}>
                {item.label}
              </Typography>
            </Box>
          );
        })}

        <Typography sx={{
          px: 2.5, pt: 3, pb: 1,
          fontSize: '0.6rem',
          color: isDark ? '#404040' : '#a3a3a3',
          textTransform: 'uppercase',
          letterSpacing: '0.15em',
          fontWeight: 700,
        }}>
          Resources
        </Typography>

        {resourceItems.map((item) => (
          <Box
            key={item.path}
            onClick={() => navigate(item.path)}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1.5,
              px: 2.5, py: 1.5,
              cursor: 'pointer',
              color: location.pathname === item.path ? accent : (isDark ? '#525252' : '#a3a3a3'),
              bgcolor: location.pathname === item.path ? sidebarActive : 'transparent',
              borderLeft: location.pathname === item.path ? `3px solid ${accent}` : '3px solid transparent',
              fontSize: '0.85rem',
              transition: 'all 0.15s',
              '&:hover': {
                color: accent,
                bgcolor: sidebarHover,
              },
            }}
          >
            {item.icon}
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, letterSpacing: '0.03em' }}>
              {item.label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export { SIDEBAR_WIDTH };
export default Sidebar;
